import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import profileApiService from './api';
import mark from '../../../../img/mark.svg';
import PhoneInput from '../../../cabinet/wizplain/widgets/phone';
import './ProfilePage.css'; // Убедитесь, что импортирован CSS файл

const ProfilePage = () => {
  const [profileData, setProfileData] = useState({
    personalInfo: {
      firstName: '',
      middleName: '',
      lastName: '',
      citizenship: 'Україна',
      taxNumber: ''
    },
    contactInfo: {
      email: '',
      phone: ''
    },
    benefits: []
  });
  
  const [isDocumentExpanded, setIsDocumentExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [validationErrors, setValidationErrors] = useState({});

  // Загрузка профиля при монтировании
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await profileApiService.getProfile();
      if (response.success) {
        setProfileData(response.data);
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: 'Помилка завантаження профілю. Можливо профіль ще не створений'
      });
    } finally {
      setLoading(false);
    }
  };

  // Функция валидации формы
  const validateForm = () => {
    const errors = {};

    // Валидация персональной информации
    if (!profileData.personalInfo.firstName.trim()) {
      errors.firstName = "Ім'я обов'язкове для заповнення";
    }

    if (!profileData.personalInfo.lastName.trim()) {
      errors.lastName = "Прізвище обов'язкове для заповнення";
    }

    if (!profileData.personalInfo.taxNumber.trim()) {
      errors.taxNumber = "РНОКПП обов'язковий для заповнення";
    } else if (profileData.personalInfo.taxNumber.length !== 10) {
      errors.taxNumber = "РНОКПП повинен містити 10 цифр";
    } else if (!/^\d+$/.test(profileData.personalInfo.taxNumber)) {
      errors.taxNumber = "РНОКПП повинен містити тільки цифри";
    }

    if (!profileData.personalInfo.citizenship.trim()) {
      errors.citizenship = "Громадянство обов'язкове для заповнення";
    }

    // Валидация контактной информации
    if (!profileData.contactInfo.email.trim()) {
      errors.email = "Email обов'язковий для заповнення";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.contactInfo.email)) {
      errors.email = "Введіть коректний email";
    }

    if (!profileData.contactInfo.phone.trim()) {
      errors.phone = "Телефон обов'язковий для заповнення";
    } else if (profileData.contactInfo.phone.replace(/\D/g, '').length < 10) {
      errors.phone = "Введіть коректний номер телефону";
    }

    return errors;
  };

  const handleInputChange = (section, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Очищаем ошибку при изменении поля
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Функция форматирования РНОКПП как в компоненте работника
  const formatRnokpp = (val) => {
    const val_match = val.match(/[0-9]/g, '');
    return val_match && val_match.join("").slice(0,10) || '';
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    // Валидация формы
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setMessage({
        type: 'danger',
        text: 'Будь ласка, заповніть всі обов\'язкові поля коректно'
      });
      
      // Прокрутка к первой ошибке
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      setValidationErrors({});

      const response = await profileApiService.saveProfile(profileData);
      
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Профіль успішно збережено'
        });
        setProfileData(response.data);
      }
    } catch (error) {
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Помилка збереження профілю'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleDocument = () => {
    setIsDocumentExpanded(!isDocumentExpanded);
  };

  // Функция для получения пропсов для поля с ошибкой
  const getFieldProps = (fieldName) => {
    return {
      className: validationErrors[fieldName] ? 'is-invalid' : '',
      isInvalid: !!validationErrors[fieldName]
    };
  };

  // Функция для отображения сообщения об ошибке
  const renderError = (fieldName) => {
    return validationErrors[fieldName] ? (
      <Form.Control.Feedback type="invalid" className="d-block">
        {validationErrors[fieldName]}
      </Form.Control.Feedback>
    ) : null;
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Завантаження...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <h1 className="page-title">Профіль</h1>
      
      {message.text && (
        <Alert variant={message.type} className="mb-4">
          {message.text}
        </Alert>
      )}

      <Form className="profile-form" onSubmit={handleSaveProfile} noValidate>
        {/* Personal Information Row */}
        <Row className="form-row">
          <Col className="form-group">
            <Form.Label htmlFor="firstName" className="form-label">
              Ім'я *
            </Form.Label>
            <Form.Control 
              type="text" 
              id="firstName" 
              value={profileData.personalInfo.firstName}
              onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
              {...getFieldProps('firstName')}
              required
            />
            {renderError('firstName')}
          </Col>
          <Col className="form-group">
            <Form.Label htmlFor="middleName" className="form-label">
              По батькові
            </Form.Label>
            <Form.Control 
              type="text" 
              id="middleName" 
              value={profileData.personalInfo.middleName}
              onChange={(e) => handleInputChange('personalInfo', 'middleName', e.target.value)}
            />
          </Col>
          <Col className="form-group">
            <Form.Label htmlFor="lastName" className="form-label">
              Прізвище *
            </Form.Label>
            <Form.Control 
              type="text" 
              id="lastName" 
              value={profileData.personalInfo.lastName}
              onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
              {...getFieldProps('lastName')}
              required
            />
            {renderError('lastName')}
          </Col>
        </Row>
        
        {/* Citizenship and Tax Number Row */}
        <Row className="form-row">
          <Col className="form-group">
            <Form.Label htmlFor="citizenship" className="form-label">
              Громадянство *
            </Form.Label>
            <Form.Select 
              id="citizenship"
              value={profileData.personalInfo.citizenship}
              onChange={(e) => handleInputChange('personalInfo', 'citizenship', e.target.value)}
              {...getFieldProps('citizenship')}
              style={{minHeight: '52px'}}
            >
              <option value="Україна">Україна</option>
              <option value="Інше">Інше</option>
            </Form.Select>
            {renderError('citizenship')}
          </Col>
          <Col className="form-group">
            <Form.Label htmlFor="taxNumber" className="form-label">
              РНОКПП *
            </Form.Label>
            <Form.Control 
              type="text" 
              id="taxNumber" 
              value={profileData.personalInfo.taxNumber}
              onChange={(e) => handleInputChange('personalInfo', 'taxNumber', formatRnokpp(e.target.value))}
              {...getFieldProps('taxNumber')}
              placeholder="3495678801"
              maxLength={10}
              required
            />
            {renderError('taxNumber')}
          </Col>
        </Row>

        {/* Contact Information Row */}
        <Row className="form-row">
          <Col className="form-group">
            <Form.Label htmlFor="email" className="form-label">
              Email *
            </Form.Label>
            <Form.Control 
              type="email" 
              id="email" 
              value={profileData.contactInfo.email}
              onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
              {...getFieldProps('email')}
              required
            />
            {renderError('email')}
          </Col>
          <Col className="form-group">
            <Form.Label className="form-label">
              Телефон *
            </Form.Label>
            <div className={validationErrors.phone ? 'phone-input-invalid' : ''}>
              <PhoneInput 
                value={profileData.contactInfo.phone} 
                onChange={(e) => handleInputChange('contactInfo', 'phone', e.target.value)}
                disabled={loading}
              />
            </div>
            {renderError('phone')}
          </Col>
        </Row>
        
        {/* Document Section */}
        {profileData.benefits.map((benefit, index) => (
          <div key={index} className={`document-section ${isDocumentExpanded ? '' : 'collapsed'}`}>
            <div className="document-header" onClick={toggleDocument} role="button">
              <div className="document-icon">
                <img src={mark} alt="Document Icon" />
              </div>
              <div className="document-title">
                <span className="doc-label">Пільга:</span>
                <span className="doc-value">{benefit.benefitType}</span>
              </div>
              <i className={`fa-solid fa-caret-down caret-icon ${isDocumentExpanded ? '' : 'rotated'}`} aria-hidden="true"></i>
            </div>
            
            {isDocumentExpanded && benefit.document && (
              <div className="document-details">
                <div className="document-info-grid">
                  <div className="doc-field">
                    <span className="doc-label">Документ:</span>
                    <span className="doc-value">{benefit.document.name}</span>
                  </div>
                  <div className="doc-field">
                    <span className="doc-label">Дата видачі:</span>
                    <span className="doc-value">
                      {benefit.document.issueDate ? 
                        new Date(benefit.document.issueDate).toLocaleDateString('uk-UA') : 
                        'Не вказано'}
                    </span>
                  </div>
                  <div className="doc-field">
                    <span className="doc-label">Номер документа:</span>
                    <span className="doc-value">{benefit.document.documentNumber}</span>
                  </div>
                  <div className="doc-field">
                    <span className="doc-label">Видано:</span>
                    <span className="doc-value">{benefit.document.issuedBy}</span>
                  </div>
                </div>
                
                {/* File Table */}
                {benefit.files && benefit.files.length > 0 && (
                  <div className="file-table">
                    <div className="table-header">
                      <div className="header-cell">Завантажений файл</div>
                      <div className="header-cell">Тип</div>
                      <div className="header-cell">Розмір</div>
                      <div className="header-cell">Дії</div>
                    </div>
                    {benefit.files.map((file, fileIndex) => (
                      <div key={fileIndex} className="table-row">
                        <div className="table-cell file-name">{file.fileName}</div>
                        <div className="table-cell file-type">{file.fileType}</div>
                        <div className="table-cell file-size">
                          {(file.fileSize / (1024 * 1024)).toFixed(2)} МБ
                        </div>
                        <div className="table-cell file-actions">
                          {/* Кнопки для действий с файлом */}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        
        {/* Save Button */}
        <Row className="form-row">
          <Col className="d-flex justify-content-end">
            <Button 
              type="submit" 
              variant="primary" 
              disabled={saving}
              className="px-4"
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Збереження...
                </>
              ) : (
                'Зберегти зміни'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ProfilePage;